/**
 * PermissionLink Component
 *
 * A link component that automatically handles permission-based rendering
 * Works with React Router Link and regular anchor tags
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';

/**
 * Permission-aware Link Component
 *
 * @example React Router Link
 * <PermissionLink
 *   permission="ZONE_DETAILS_VIEW"
 *   to="/zones/123"
 * >
 *   View Details
 * </PermissionLink>
 *
 * @example Regular anchor tag
 * <PermissionLink
 *   permission="ZONE_EXPORT"
 *   href="/api/zones/export"
 *   download
 * >
 *   Download Zones
 * </PermissionLink>
 *
 * @example Disable instead of hide
 * <PermissionLink
 *   permission="ZONE_UPDATE"
 *   to="/zones/123/edit"
 *   strategy="disable"
 * >
 *   Edit Zone
 * </PermissionLink>
 */
export const PermissionLink = ({
  permission,
  mode = 'any',
  strategy = 'hide', // 'hide' or 'disable'
  to, // React Router Link
  href, // Regular anchor
  children,
  className = '',
  ...linkProps
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermission();

  // Check permission
  let hasRequiredPermission = true;

  if (permission) {
    if (Array.isArray(permission)) {
      hasRequiredPermission = mode === 'all'
        ? hasAllPermissions(permission)
        : hasAnyPermission(permission);
    } else {
      hasRequiredPermission = hasPermission(permission);
    }
  }

  // If no permission and strategy is hide, don't render
  if (!hasRequiredPermission && strategy === 'hide') {
    return null;
  }

  // If no permission and strategy is disable, render disabled link
  if (!hasRequiredPermission && strategy === 'disable') {
    return (
      <span
        className={`${className} disabled-link`}
        style={{
          cursor: 'not-allowed',
          opacity: 0.5,
          textDecoration: 'none',
          color: '#6c757d',
        }}
        title="You do not have permission"
      >
        {children}
      </span>
    );
  }

  // Has permission, render appropriate link type
  if (to) {
    // React Router Link
    return (
      <Link to={to} className={className} {...linkProps}>
        {children}
      </Link>
    );
  }
  if (href) {
    // Regular anchor tag
    return (
      <a href={href} className={className} {...linkProps}>
        {children}
      </a>
    );
  }
  // Fallback to span
  return (
    <span className={className} {...linkProps}>
      {children}
    </span>
  );
};

PermissionLink.propTypes = {
  permission: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  mode: PropTypes.oneOf(['any', 'all']),
  strategy: PropTypes.oneOf(['hide', 'disable']),
  to: PropTypes.string,
  href: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default PermissionLink;
